import { checkRevelationEmailState } from '@aibees/elephant/components/people-contact-data/utils';
import { useIsPeopleUnlockButtonLoading } from '@aibees/elephant/components/people-unlock/hooks/use-is-people-unlock-button-loading';
import { QUICK_INFO_BUTTONS } from '@aibees/elephant/components/quick-info-group/components/quick-info-button';
import {
  ProcessingItemState,
  useActionButtonContext,
} from '@aibees/elephant/context/action-button-context';
import { useSearchTypeFromRoute, useTrackEvents } from '@aibees/elephant/hooks';
import {
  SaveRecordAction,
  SaveRecordRequest,
} from '@aibees/elephant/hooks/types';
import { useSaveRecord } from '@aibees/elephant/hooks/use-save-record';
import { usePersonUpdatesStore } from '@aibees/elephant/store/person-updates';
import { SearchType } from '@aibees/elephant/types';
import { notificationIconMap } from '@aibees/elephant/utils';
import { useAuthContext } from '@aibees/shared/auth';
import { CreditUsageType } from '@aibees/shared/definition';
import { useNotify } from '@aibees/shared/hooks';
import { baseQueryClient } from '@aibees/shared/http';
import { useWorkspaceUsers } from '@aibees/shared/workspace';
import { random } from 'lodash-es';
import { MouseEventHandler } from 'react';
import { PeopleTableInfo } from '../../../types';
import { isJobChangeHappened } from '../../people-drawer/components/contact-details/utils';
import { doesEmailEnrichmentHaveAnyUpdates } from '../../people-quick-info/utils';
import {
  isAnyEmailNeedsReverification,
  isEmailNotFoundRetryable,
} from '../utils';

(window as any).baseQueryClient = baseQueryClient;

export function useHandleUnlockEmails(item?: PeopleTableInfo) {
  const users = useWorkspaceUsers(true);
  const { user } = useAuthContext();
  const selectedUser = users.find((u) => u.id === user?.id);
  const searchType = useSearchTypeFromRoute();
  const { track } = useTrackEvents();
  const { mutateAsync } = useSaveRecord(SearchType.People);
  const notify = useNotify();
  const { emailLoading } = useIsPeopleUnlockButtonLoading(item);
  const unlocked = checkRevelationEmailState(item);
  const { setEmailsUpdateTriggered } = usePersonUpdatesStore();
  const { addProcessingItems, removeProcessingItems } =
    useActionButtonContext();
  const hasUpdate = doesEmailEnrichmentHaveAnyUpdates(item);
  // No found-email precondition: a never-unlocked contact whose company changed
  // must get the combined action too, not a plain Unlock.
  const needsEnrichEmail = isJobChangeHappened(item);
  const notFoundRetryable = isEmailNotFoundRetryable(item);
  const showReverify =
    !hasUpdate &&
    !needsEnrichEmail &&
    item?.revelation?.email &&
    isAnyEmailNeedsReverification(item?.revelation?.email);
  // Same precedence as the action button in unlock-emails.tsx, and as the three
  // render-boundary status derivations: a company change outranks the email's own
  // state. A record with both an email update and a job change sends
  // CONTACT_ENRICH_EMAIL, so it has to quote that rate and not re-verification's.
  const creditType = needsEnrichEmail
    ? CreditUsageType.CONTACT_ENRICH_EMAIL
    : hasUpdate || showReverify
    ? CreditUsageType.CONTACT_EMAIL_REVERIFICATION
    : CreditUsageType.CONTACT_EMAIL_FINDING;

  const handleReverify = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation();
    e.preventDefault();
    if (item?.id) {
      setEmailsUpdateTriggered(true);
      addProcessingItems({
        id: item.id,
        type: QUICK_INFO_BUTTONS.EMAIL_ENRICHMENT,
        state: ProcessingItemState.REVERIFYING,
      });
      const key = Date.now().toString();

      mutateAsync({
        kind: 'ADD',
        ids: [item?.id],
        actions: [SaveRecordAction.CONTACT_REVERIFY_EMAIL],
        overwriteOwner: false,
        user: selectedUser,
        labels: [],
      }).then(() => {
        notify.progress(
          'Your request is in progress',
          'Processing the contact details. Please wait...',
          key,
          {
            icon: notificationIconMap.ContactDetails,
          }
        );
        track('UNLOCK_EMAIL', { multiple: false, reverify: true, searchType });
      });
    }
  };

  // "Update Profile & Find Email". One action, not two: the server runs the
  // job-change profile update itself when it receives CONTACT_ENRICH_EMAIL, then
  // re-runs Find Email against the new current company — so sending
  // CONTACT_UPDATE_JOB_CHANGE alongside it would double up.
  const handleEnrichEmail: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (item?.id) {
      setEmailsUpdateTriggered(true);
      addProcessingItems({
        id: item.id,
        type: QUICK_INFO_BUTTONS.EMAIL_ENRICHMENT,
        state: ProcessingItemState.CHECKING_FOR_UPDATE,
      });
      addProcessingItems({
        id: item.id,
        type: QUICK_INFO_BUTTONS.CONTACT_ENRICHMENT,
        // The profile half is the job-change update the server adds, so this is
        // UPDATING_JOB_CHANGE, not UPDATING. On a locked profile that is the
        // difference between the spinner clearing and spinning forever:
        // revelation.contact.state is never set, and useCheckContactDetailsProcessing
        // only has a changes.CONTACT-based fallback for UPDATING_JOB_CHANGE.
        state: ProcessingItemState.UPDATING_JOB_CHANGE,
      });

      mutateAsync({
        kind: 'ADD',
        ids: [item?.id],
        actions: [SaveRecordAction.CONTACT_ENRICH_EMAIL],
        overwriteOwner: false,
        user: selectedUser,
        labels: [],
      })
        .then(() => {
          notify.complete(
            'Your request is in progress',
            'Processing the contact details. Please wait...',
            random(1, 101),
            {
              icon: notificationIconMap.ContactDetails,
            }
          );
          track('UNLOCK_EMAIL', { multiple: false, update: true, searchType });
        })
        .catch(() => {
          // Both spinners have to be torn down by hand. Neither completion
          // signal can fire on a failed run: revelation.email.state never moves,
          // and the changes.COMPANY / changes.CONTACT fallbacks both wait for
          // the server to clear a flag it never set — so without this they spin
          // until the page is reloaded. The error notification is already
          // raised by useBaseMutation.
          removeProcessingItems({
            id: item.id,
            type: QUICK_INFO_BUTTONS.EMAIL_ENRICHMENT,
          });
          removeProcessingItems({
            id: item.id,
            type: QUICK_INFO_BUTTONS.CONTACT_ENRICHMENT,
          });
        });
    }
  };

  const handleUpdate: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (item?.id) {
      setEmailsUpdateTriggered(true);
      addProcessingItems({
        id: item.id,
        type: QUICK_INFO_BUTTONS.EMAIL_ENRICHMENT,
        state: ProcessingItemState.UPDATING,
      });

      mutateAsync({
        kind: 'ADD',
        ids: [item?.id],
        actions: [SaveRecordAction.CONTACT_REVEAL_EMAIL],
        overwriteOwner: false,
        user: selectedUser,
        labels: [],
      }).then(() => {
        notify.complete(
          'Your request is in progress',
          'Processing the contact details. Please wait...',
          random(1, 101),
          {
            icon: notificationIconMap.ContactDetails,
          }
        );
        track('UNLOCK_EMAIL', { multiple: false, update: true, searchType });
      });
    }
  };

  const handleUnlock: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (emailLoading) return;

    if (item?.id) {
      const payload: SaveRecordRequest = {
        kind: 'ADD',
        actions: [SaveRecordAction.CONTACT_REVEAL_EMAIL],
        ids: [item?.id],
        overwriteOwner: false,
        user: selectedUser,
        labels: [],
      };
      addProcessingItems({
        id: item.id,
        type: QUICK_INFO_BUTTONS.EMAIL_ENRICHMENT,
        state: ProcessingItemState.UNLOCKING,
      });
      const key = Date.now().toString();

      mutateAsync(payload).then((data) => {
        notify.progress(
          'Your request is in progress',
          'Processing the contact details. Please wait...',
          key,
          {
            icon: notificationIconMap.ContactDetails,
          }
        );
        track('UNLOCK_EMAIL', { multiple: false, sidebar: false, searchType });
      });
    }
  };

  return {
    handleReverify,
    handleUpdate,
    handleEnrichEmail,
    hasUpdate,
    needsEnrichEmail,
    notFoundRetryable,
    showReverify,
    unlocked,
    handleUnlock,
    loading: emailLoading,
    creditType,
  };
}
